import { runWhenIdle } from '@infinitro/global/idle';
import type * as SentrySdk from '@sentry/sveltekit';

type Sentry = typeof SentrySdk;
type SentryOptions = Parameters<Sentry['init']>[0];

export const MAX_PENDING_ERRORS = 50;
const SENTRY_IDLE_TIMEOUT_MS = 4_000;

let sentry: Sentry | null = null;
const pendingErrors: unknown[] = [];

/** Sends `error` to Sentry, holding it until the SDK has downloaded. */
export const reportError = (error: unknown): void => {
	if (sentry) {
		sentry.captureException(error);
		return;
	}
	if (pendingErrors.length >= MAX_PENDING_ERRORS) pendingErrors.shift();
	pendingErrors.push(error);
};

const onWindowError = (event: ErrorEvent) => reportError(event.error ?? event.message);
const onUnhandledRejection = (event: PromiseRejectionEvent) => reportError(event.reason);

const stopBufferingWindowErrors = () => {
	window.removeEventListener('error', onWindowError);
	window.removeEventListener('unhandledrejection', onUnhandledRejection);
};

const isTouchDevice = (): boolean => window.matchMedia?.('(pointer: coarse)').matches ?? false;

const addReplay = (sdk: Sentry) =>
	sdk
		.lazyLoadIntegration('replayIntegration')
		.then((replayIntegration) => sdk.addIntegration(replayIntegration()))
		.catch((error: unknown) => console.warn('[sentry] session replay failed to load', error));

const startSentry = async (options: SentryOptions): Promise<void> => {
	try {
		const sdk = await import('@sentry/sveltekit');
		sdk.init(options);
		stopBufferingWindowErrors();
		sentry = sdk;
		pendingErrors.splice(0).forEach((error) => sdk.captureException(error));
		if (!isTouchDevice()) void addReplay(sdk);
	} catch (error) {
		console.warn('[sentry] failed to load', error);
	}
};

/** Starts Sentry off the critical path; errors raised before it loads are replayed to it. */
export const initErrorReporting = (options: SentryOptions): void => {
	window.addEventListener('error', onWindowError);
	window.addEventListener('unhandledrejection', onUnhandledRejection);
	runWhenIdle(() => startSentry(options), SENTRY_IDLE_TIMEOUT_MS);
};
